import { Injectable } from '@angular/core';
import { CvConfigurationService } from './cv-configuration.service';

@Injectable({
    providedIn: 'root'
})
export class ModuleThemesService {

    /*
        ModuleThemes is a collection of theme objects used in charts and graphs.
        Created globally to allow theme consistency and easier maintenance.

        Themes should be at least 10 colors in length to maintain reusability across all graphs and charts. If a chart needs more, loop through the colors.
        Theme-state data (defaults, current theme, special case themes) should be handled in the module's logic, not here.
        Mark special case themes with comments and/or in a sub-collection; not in the theme name.

        Themes are set up as an object to allow for Key:Value retrieval (e.g., ModuleThemes['darktheme']).
            Key: Name of the module.
            Value: An array of RGBA strings.
            While we use Chart.js, values should use RGBA (red, green, blue, alpha) format, not hexadecimal.
                i.e., rgba(11,22,33,0.85)

        Names should be kept consistent in format.
            * Short and sweet names (e.g., 'dark', 'light', 'summer', 'neon', 'seafoam').
            * Avoid using generic terms in the names, such as: default, theme, module. If absolutely needed, special type collections (such as a hexadecimal version of an existing RGBA-based theme), may be marked as such. ('SummerTheme', 'SummerThemeHex')
            * Collections can use the word "collection." (e.g. "HolidayCollection", "NeonCollection")
            * Pascal Case (AllWordsStartWithACapital)
            * No symbols (hyphens, underscores). Put symbols in the Name property.
    */
    ModuleThemes = [
        {
            Name: 'Default',
            Colors: [
                'rgba(211,27,88,0.7)',   //#D31B58
                'rgba(107,29,96,0.7)',   //#6B1D60
                'rgba(37,49,76,0.7)',    //#25314C
                'rgba(36,79,84,0.7)',    //#244F54
                'rgba(7,145,135,0.7)',   //#079187
                'rgba(255,218,64,0.7)',  //#FFDA40
                'rgba(221,168,49,0.7)',  //#DDA831
                'rgba(233,110,76,0.7)',  //#E96E4C
                'rgba(232,180,175,0.7)', //#E8B4AF
                'rgba(98,179,183,0.7)',  //#62B3B7
            ]
        },
        {
            Name: 'Rainbow',
            Colors: [
                'rgba(50,69,113,0.7)',
                'rgba(53,148,161,0.7)',
                'rgba(236,163,38,0.7)',
                'rgba(227,105,34,0.7)',
                'rgba(159,1,12,0.7)',
                'rgba(92,61,128,0.7)',
                'rgba(249,106,104,0.7)',
                'rgba(89,187,214,0.7)',
                'rgba(148,178,46,0.7)',
                'rgba(254,207,52,0.7)',
            ]
        },
        //{
        //    Name: 'Tol-Rainbow CBF', //CBF = Color Blind Friendly
        //    Colors: [
        //        'rgba(120,28,129,0.7)',
        //        'rgba(67, 50, 141,0.7)',
        //        'rgba(65,111,184,0.7)',
        //        'rgba(81,156,184,0.7)',
        //        'rgba(112,180,132,0.7)',
        //        'rgba(153,189,92,0.7)',
        //        'rgba(195,186,69,0.7)',
        //        'rgba(224,162,57,0.7)',
        //        'rgba(230,107,45,0.7)',
        //        'rgba(217,33,32,0.7)',
        //    ]
        //},
        {
            Name: 'Neutral',
            Colors: [
                'rgba(109,46,48,0.7)',
                'rgba(47,52,59,0.7)',
                'rgba(126,130,122,0.7)',
                'rgba(226,204,166,0.7)',
                'rgba(195,119,102,0.7)',
                'rgba(140,128,142,0.7)',
                'rgba(232,187,161,0.7)',
                'rgba(107,128,139,0.7)',
                'rgba(70,80,71,0.7)',
                'rgba(126,102,108,0.7)',
            ]
        },
        {
            Name: 'Beach Time',
            Colors: [
                'rgba(249,106,104,0.7)',
                'rgba(181,230,229,0.7)',
                'rgba(253,203,98,0.7)',
                'rgba(155,207,181,0.7)',
                'rgba(254,237,176,0.7)',
                'rgba(108,141,151,0.7)',
                'rgba(184,193,165,0.7)',
                'rgba(248,164,121,0.7)',
                'rgba(89,187,214,0.7)',
                'rgba(213,204,144,0.7)',
            ]
        },
        {
            Name: 'Kayak',
            Colors: [
                'rgba(54,54,44,0.7)',
                'rgba(98,146,126,0.7)',
                'rgba(169,173,130,0.7)',
                'rgba(229,211,169,0.7)',
                'rgba(128,84,53,0.7)',
                'rgba(8,62,91,0.7)',
                'rgba(68,162,145,0.7)',
                'rgba(110,101,84,0.7)',
                'rgba(219,229,221,0.7)',
                'rgba(215,216,156,0.7)',
            ]
        },
        {
            Name: 'Magic',
            Colors: [
                'rgba(55,43,93,0.7)',
                'rgba(12,26,35,0.7)',
                'rgba(52,122,158,0.7)',
                'rgba(170,27,120,0.7)',
                'rgba(34,135,128,0.7)',
                'rgba(102,25,120,0.7)',
                'rgba(46,50,68,0.7)',
                'rgba(73,194,222,0.7)',
                'rgba(9,2,66,0.7)',
                'rgba(192,140,210,0.7)',
            ]
        },
        {
            Name: 'Sunset Moon',
            Colors: [
                'rgba(0,63,92,0.7)',
                'rgba(249,93,106,0.7)',
                'rgba(255,124,67,0.7)',
                'rgba(255,166,0,0.7)',
                'rgba(160,81,149,0.7)',
                'rgba(24,87,104,0.7)',
                'rgba(212,80,135,0.7)',
                'rgba(234,66,27,0.7)',
                'rgba(239,135,0,0.7)',
                'rgba(117,0,95,0.7)',
            ]
        },
        {
            Name: 'Vibrant',
            Colors: [
                'rgba(97,151,226,0.7)',
                'rgba(185,229,46,0.7)',
                'rgba(228,55,187,0.7)',
                'rgba(255,153,18,0.7)',
                'rgba(255,45,90,0.7)',
                'rgba(20,67,219,0.7)',
                'rgba(247,43,160,0.7)',
                'rgba(247,62,25,0.7)',
                'rgba(252,189,11,0.7)',
                'rgba(142,15,232,0.7)',
            ]
        },
        {
            Name: 'Poppy',
            Colors: [
                'rgba(0,135,108,0.7)',
                'rgba(168,186,97,0.7)',
                'rgba(223,168,80,0.7)',
                'rgba(222,99,71,0.7)',
                'rgba(212,61,81,0.7)',
                'rgba(67,175,156,0.7)',
                'rgba(120,171,99,0.7)',
                'rgba(218,199,103,0.7)',
                'rgba(225,135,69,0.7)',
                'rgba(57,150,85,0.7)',
            ]
        },
        {
            Name: 'Seafoam',
            Colors: [
                'rgba(154,210,217,0.7)',
                'rgba(5,191,191,0.7)',
                'rgba(93,166,21,0.7)',
                'rgba(171,188,49,0.7)',
                'rgba(57,135,105,0.7)',
                'rgba(206,241,158,0.7)',
                'rgba(167,221,167,0.7)',
                'rgba(120,190,151,0.7)',
                'rgba(57,134,137,0.7)',
                'rgba(10,71,108,0.7)',
            ]
        },
        //{
        //    Name: 'Rainbow V2',
        //    Colors: [
        //        'rgba(120,27,129,0.7)',
        //        'rgba(64,111,184,0.7)',
        //        'rgba(112,180,131,0.7)',
        //        'rgba(224,162,57,0.7)',
        //        'rgba(217,33,33,0.7)',
        //        'rgba(67,50,141,0.7)',
        //        'rgba(81,156,183,0.7)',
        //        'rgba(152,189,92,0.7)',
        //        'rgba(230,106,45,0.7)',
        //        'rgba(67,175,156,0.7)',
        //    ]
        //},
        {
            Name: 'Sedona',
            Colors: [
                'rgba(217,90,43,0.7)',
                'rgba(242,147,91,0.7)',
                'rgba(143,65,47,0.7)',
                'rgba(32,76,114,0.7)',
                'rgba(77,115,115,0.7)',
                'rgba(107,202,176,0.7)',
                'rgba(15,114,88,0.7)',
                'rgba(191,103,82,0.7)',
                'rgba(255,160,139,0.7)',
                'rgba(199,170,164,0.7)',
            ]
        },
        {
            Name: 'Scala',
            Colors: [
                'rgba(244,115,32,0.7)',
                'rgba(14,49,103,0.7)',
                'rgba(143,148,156,0.7)',
                'rgba(244,115,32,0.7)',
                'rgba(14,49,103,0.7)',
                'rgba(143,148,156,0.7)',
                'rgba(244,115,32,0.7)',
                'rgba(14,49,103,0.7)',
                'rgba(143,148,156,0.7)',
                'rgba(244,115,32,0.7)',
                'rgba(14,49,103,0.7)',
                'rgba(143,148,156,0.7)',
            ]
        },
    ];

    private _ModuleThemeNames = null;
    private _ModuleThemeMenuOptions = null;
    constructor(
        private dashboardManager: CvConfigurationService
    ) { }

    GetModuleThemeNames() {
        if (this._ModuleThemeNames == null)
            this._ModuleThemeNames = this.ModuleThemes.map(t => t.Name);

        return this._ModuleThemeNames;
    }

    GetModuleThemesMenu() {
        if (this._ModuleThemeMenuOptions == null) {
            this._ModuleThemeMenuOptions = [];
        if (this.dashboardManager.isCxOne){
            this.ModuleThemes.push({
                Name: 'CXOne',
                Colors: [
                    'rgba(132,145,195,0.7)',
                    'rgba(189,122,178,0.7)',
                    'rgba(96,149,162,0.7)',
                    'rgba(137,154,60,0.7)',
                    'rgba(159,132,195,0.7)',
                    'rgba(189,122,134,0.7)',
                    'rgba(95,165,80,0.7)',
                    'rgba(177,131,101,0.7)',
                    'rgba(70,160,119,0.7)',
                    'rgba(151,142,134,0.7)',
                ]
            });
        }
            for (let i = 0; i < this.ModuleThemes.length; i++) {
                this._ModuleThemeMenuOptions.push({
                    themeIndex: i,
                    name: this.ModuleThemes[i].Name,
                    icon: 'themeIcon-' + this.ModuleThemes[i].Name.toLowerCase().split(' ').join(''),
                    click: null //You need to manually assign this to your controller's click handler.
                });
            }
        }

        return JSON.parse(JSON.stringify(this._ModuleThemeMenuOptions)); //Return a deep-copied version of the menu. (Prevents reference errors causing modules to update a neighboring module.)
    }

    GetThemeByName(name) {
        return this.ModuleThemes.filter(t => t.Name === name)[0];
    }

    GetThemeByIndex(index) {
        return this.ModuleThemes[index].Colors;
    }

}
